import SEOHelmet from "../components/SEOHelmet";
import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import NotificationBell from '../components/NotificationBell';
import OnboardingTour from '../components/OnboardingTour';
import WorkerOnboarding3Step from '../components/WorkerOnboarding3Step';
import NextShiftCard from '../components/NextShiftCard';
import { ShiftComplianceIndicators } from '../components/TrustBadges';
import BetaWelcomeModal from '../components/BetaWelcomeModal';
import WelfareCheckModal from '../components/WelfareCheckModal';
import LicencesPage from './LicencesPage';
import ProfileQualificationAgent from '../components/ProfileQualificationAgent';

const api = () => axios.create({
  baseURL: '/api',
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

// ─── Colour system (matches brand) ─────────────────────────────
const INDUSTRY_COLORS = {
  hospitality: 'bg-amber-50 text-amber-700 border-amber-200',
  construction: 'bg-blue-50 text-blue-700 border-blue-200',
  retail: 'bg-pink-50 text-pink-700 border-pink-200',
  events: 'bg-purple-50 text-purple-700 border-purple-200',
  warehouse: 'bg-orange-50 text-orange-700 border-orange-200',
  security: 'bg-slate-50 text-slate-700 border-slate-200',
  cleaning: 'bg-teal-50 text-teal-700 border-teal-200',
  general: 'bg-neutral-50 text-neutral-600 border-neutral-200',
};

const STATUS_META = {
  pending:  { label: 'Reviewing',     cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  hired:    { label: 'Hired',         cls: 'bg-brand-50 text-brand-700 border-brand-200' },
  declined: { label: 'Not selected',  cls: 'bg-neutral-100 text-neutral-500 border-neutral-200' },
};

const INDUSTRIES = ['All','Hospitality','Construction','Retail','Events','Warehouse','Security','General'];

const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const TIME_SLOTS = ['Morning','Afternoon','Evening','Night'];
const CANCEL_REASONS = [
  { id: 'sick', label: 'I\'m sick', icon: '🤒' },
  { id: 'emergency', label: 'Family emergency', icon: '🚨' },
  { id: 'transport', label: 'Transport issue', icon: '🚗' },
  { id: 'other', label: 'Other reason', icon: '💬' },
];

// ─── Availability Calendar Component ─────────────────────────────
function AvailabilityCalendar({ onBack }) {
  const [availability, setAvailability] = useState(() => {
    try {
      const saved = localStorage.getItem('sm_availability');
      const parsed = saved ? JSON.parse(saved) : {};
      return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : {};
    } catch {
      try { localStorage.removeItem('sm_availability'); } catch { /* ignore */ }
      return {};
    }
  });
  const [toast, setToast] = useState('');

  const toggleDay = (day) => {
    setAvailability(prev => {
      const next = { ...prev };
      if (next[day]) { delete next[day]; } else { next[day] = ['Morning', 'Afternoon']; }
      return next;
    });
  };

  const toggleTime = (day, time) => {
    setAvailability(prev => {
      const next = { ...prev };
      if (!next[day]) next[day] = [];
      if (next[day].includes(time)) {
        next[day] = next[day].filter(t => t !== time);
        if (next[day].length === 0) delete next[day];
      } else {
        next[day] = [...next[day], time];
      }
      return next;
    });
  };

  const save = () => {
    try {
      localStorage.setItem('sm_availability', JSON.stringify(availability));
    } catch {
      setToast('Could not save — storage full');
      setTimeout(() => setToast(''), 2500);
      return;
    }
    setToast('Availability saved!');
    setTimeout(() => setToast(''), 2500);
  };

  return (
    <div className="animate-fade-in">
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] px-5 py-2.5 rounded-2xl text-sm font-semibold shadow-elevated glass animate-slide-up text-brand-600">
          {toast}
        </div>
      )}
      <div className="flex items-center gap-2 mb-4">
        <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-neutral-100 transition-all">
          <svg className="w-4 h-4 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
        </button>
        <h2 className="font-brand text-lg font-bold text-neutral-900">Set Availability</h2>
      </div>
      <p className="text-sm text-neutral-500 mb-4">Tap days you're available, then select preferred times. Employers see this on your profile.</p>
      <div className="space-y-2 mb-5">
        {DAYS.map(day => {
          const isActive = !!availability[day];
          return (
            <div key={day} className="card p-3">
              <button onClick={() => toggleDay(day)} className="w-full flex items-center justify-between">
                <span className={`text-sm font-bold ${isActive ? 'text-brand-600' : 'text-neutral-400'}`}>{day}</span>
                <div className={`w-8 h-5 rounded-full transition-all ${isActive ? 'bg-brand-500' : 'bg-neutral-200'}`}>
                  <div className={`w-4 h-4 bg-white rounded-full shadow mt-0.5 transition-all ${isActive ? 'ml-3.5' : 'ml-0.5'}`} />
                </div>
              </button>
              {isActive && (
                <div className="flex flex-wrap gap-1.5 mt-2.5 pt-2 border-t border-neutral-50">
                  {TIME_SLOTS.map(time => {
                    const selected = availability[day]?.includes(time);
                    return (
                      <button key={time} onClick={() => toggleTime(day, time)}
                        className={`text-[11px] px-2.5 py-1 rounded-full font-medium border transition-all ${selected ? 'bg-brand-50 text-brand-700 border-brand-200' : 'bg-neutral-50 text-neutral-400 border-neutral-100'}`}>
                        {time}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <button onClick={save} className="w-full py-3 bg-brand-500 hover:bg-brand-600 text-white rounded-xl font-bold text-sm transition-all active:scale-95">
        Save Availability
      </button>
    </div>
  );
}

// ─── Shift Cancellation Modal ─────────────────────────────────
function CancelShiftModal({ app, onCancel, onClose }) {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!reason) return;
    setSubmitting(true);
    try {
      await axios.create({
        baseURL: '/api',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      }).put(`/applications/${app.id}/withdraw`, { cancel_reason: reason, cancel_details: details });
      onCancel();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not cancel shift');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5 animate-slide-up" onClick={e => e.stopPropagation()}>
        <h3 className="font-brand text-lg font-bold text-neutral-900 mb-1">Can't make it?</h3>
        <p className="text-sm text-neutral-500 mb-4">Let the employer know why so they can find a replacement.</p>
        <div className="space-y-2 mb-4">
          {CANCEL_REASONS.map(r => (
            <button key={r.id} onClick={() => setReason(r.id)}
              className={`w-full p-3 rounded-xl border text-left flex items-center gap-3 transition-all ${reason === r.id ? 'border-brand-400 bg-brand-50' : 'border-neutral-100 hover:border-neutral-200'}`}>
              <span className="text-lg">{r.icon}</span>
              <span className={`text-sm font-medium ${reason === r.id ? 'text-brand-700' : 'text-neutral-700'}`}>{r.label}</span>
            </button>
          ))}
        </div>
        {reason === 'other' && (
          <textarea value={details} onChange={e => setDetails(e.target.value)} placeholder="Tell us more..."
            className="input w-full text-sm p-3 mb-4" rows={2} />
        )}
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 bg-neutral-100 text-neutral-600 rounded-xl font-semibold text-sm">Keep shift</button>
          <button onClick={submit} disabled={!reason || submitting}
            className="flex-1 py-2.5 bg-rose-500 hover:bg-rose-600 text-white rounded-xl font-bold text-sm disabled:opacity-50 transition-all">
            {submitting ? 'Cancelling...' : 'Cancel shift'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Swipeable shift card ───────────────────────────────────────
function SwipeCard({ shift, applied, saved, onApply, onSave, onSkip, urgent = false, distance = null }) {
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [gone, setGone] = useState(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const swipeDirection = useRef(null); // 'horizontal' | 'vertical' | null
  const industry = (shift.industry || 'general').toLowerCase();
  const indColor = INDUSTRY_COLORS[industry] || INDUSTRY_COLORS.general;
  const hourlyRate = Math.round(shift.hourly_rate || 0);
  const hours = shift.hours || shift.duration || 0;
  const title = (shift.title || '').replace(/^URGENT:\s*/i, '');
  const dateStr = shift.date ? new Date(shift.date).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }) : '';
  const timeStr = shift.start_time && shift.end_time ? `${shift.start_time.slice(0,5)}–${shift.end_time.slice(0,5)}` : '';
  const certs = Array.isArray(shift.required_certs) ? shift.required_certs.filter(Boolean) : [];

  const onTouchStart = e => {
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    swipeDirection.current = null;
    setDragging(true);
  };
  const onTouchMove = e => {
    if (!dragging) return;
    const moveX = e.touches[0].clientX - startX.current;
    const moveY = e.touches[0].clientY - startY.current;
    // Lock direction once threshold is met
    if (swipeDirection.current === null) {
      if (Math.abs(moveX) > 15 && Math.abs(moveX) > Math.abs(moveY)) {
        swipeDirection.current = 'horizontal';
      } else if (Math.abs(moveY) > 10) {
        swipeDirection.current = 'vertical';
      }
    }
    // Only set dx for horizontal swipes — let vertical scroll naturally
    if (swipeDirection.current === 'horizontal') {
      e.preventDefault();
      setDx(moveX);
    }
  };
  const onTouchEnd = () => {
    setDragging(false);
    if (swipeDirection.current === 'horizontal') {
      if (dx > 90 && !applied) { setGone(true); setTimeout(() => onApply(shift), 200); }
      else if (dx < -90) { setGone(true); setTimeout(() => onSkip?.(shift.id), 200); }
      else setDx(0);
    } else {
      setDx(0);
    }
    swipeDirection.current = null;
  };

  if (gone) return null;

  const rot = dx * 0.05;
  const opacity = Math.max(0.2, 1 - Math.abs(dx) / 300);
  const cardStyle = dx !== 0 ? { transform: `translateX(${dx}px) rotate(${rot}deg)`, opacity, transition: dragging ? 'none' : 'all 0.3s ease' } : {};

  return (
    <div
      className="card p-4 relative select-none touch-pan-y"
      style={cardStyle}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Swipe hints */}
      {dx > 30 && <div className="absolute left-3 top-1/2 -translate-y-1/2 bg-brand-500 text-white text-[11px] font-bold px-2.5 py-1 rounded-lg z-10">Apply</div>}
      {dx < -30 && <div className="absolute right-3 top-1/2 -translate-y-1/2 bg-neutral-200 text-neutral-600 text-[11px] font-bold px-2.5 py-1 rounded-lg z-10">Skip</div>}

      {/* Urgent banner */}
      {urgent && <div className="-mx-4 -mt-4 mb-3 px-4 py-1.5 bg-rose-50 border-b border-rose-100 rounded-t-2xl text-[11px] font-semibold text-rose-600">Same day — filling fast</div>}

      {/* Payment trust badge at bottom */}
      {!shift.fast_pay && (
        <div className="-mx-4 -mb-4 mt-3 px-4 py-1.5 bg-neutral-50 border-t border-neutral-100 rounded-b-2xl text-[10px] text-neutral-400 font-medium text-center">💰 Paid within 48hrs · Escrow protected</div>
      )}
      {shift.fast_pay && (
        <div className="-mx-4 -mb-4 mt-3 px-4 py-1.5 bg-amber-50 border-t border-amber-100 rounded-b-2xl text-[10px] text-amber-600 font-semibold text-center">⚡ Fast Pay — same day payment</div>
      )}

      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0 pr-3">
          {/* Industry chip */}
          <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full border mb-1.5 ${indColor}`}>
            {industry.charAt(0).toUpperCase() + industry.slice(1)}
          </span>

          {/* Title */}
          <h3 className="text-[15px] font-bold text-neutral-900 leading-tight mb-1">{title}</h3>

          {/* Location + hours */}
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12px] text-neutral-400">
            {shift.location && (
              <span className="flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                {shift.location}
              </span>
            )}
            {hours > 0 && <><span className="text-neutral-200">·</span><span>{hours}h</span></>}
            {shift.applicant_count > 0 && <><span className="text-neutral-200">·</span><span className="text-amber-500 font-medium">{shift.applicant_count} applying</span></>}
          </div>

          {/* Trust & Compliance Badges */}
          <div className="flex flex-wrap gap-1.5 mt-2.5 mb-2">
            {hourlyRate >= 155 && (
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                ✓ Award+
              </div>
            )}
            {shift.fair_work_verified && (
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                🔒 Fair Work
              </div>
            )}
          </div>

          {/* Date + time */}
          {(dateStr || timeStr) && (
            <p className="text-[11px] text-neutral-400 mt-1">{dateStr}{dateStr && timeStr ? ' · ' : ''}{timeStr}</p>
          )}

          {/* Cert chips + badges */}
          <div className="flex flex-wrap gap-1 mt-2">
            {certs.length > 0 && certs.map(c => <span key={c} className="text-[10px] px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full font-medium">{c}</span>)}
            {(shift.hourly_rate || 0) >= 23.23 && (
              <span className="text-[10px] px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full font-medium">✓ Award+</span>
            )}
            {shift.fast_pay && (
              <span className="text-[10px] px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full font-medium">💰 Fast Pay</span>
            )}
            {distance !== null && (
              <span className="text-[10px] px-2 py-0.5 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-full font-medium">📍 {distance < 1 ? `${(distance*1000).toFixed(0)}m` : `${distance.toFixed(1)}km`}</span>
            )}
          </div>
        </div>

        {/* Rate + actions */}
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <div className="text-right">
            <span className="text-[22px] font-extrabold text-neutral-900 tabular-nums leading-none">${hourlyRate}</span>
            <span className="text-[11px] text-neutral-400">/hr</span>
          </div>
          <div className="flex gap-1.5 items-center">
            <button
              onClick={() => onSave(shift.id)}
              className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all active:scale-90 ${saved ? 'bg-rose-50 text-rose-400' : 'bg-neutral-50 text-neutral-300 hover:text-neutral-400'}`}
            >
              <svg className="w-4 h-4" fill={saved ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg>
            </button>
            <button
              onClick={() => onApply(shift)}
              disabled={applied}
              className={`px-3.5 py-2 rounded-xl text-[12px] font-bold transition-all active:scale-95 ${applied ? 'bg-brand-50 text-brand-600 border border-brand-200' : 'bg-brand-500 hover:bg-brand-600 text-white shadow-button'}`}
            >
              {applied ? 'Applied' : 'Apply'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Search bar component ──────────────────────────────────────
function SearchBar({ searchQuery, setSearchQuery, industryFilter, setIndustryFilter, payRange, setPayRange, sortBy, setSortBy, hasLocation = false }) {
  return (
    <div className="space-y-2 mb-4">
      <div className="relative">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
        <input type="text" placeholder="Search shifts..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
          className="input w-full pl-9 pr-4 py-2.5 text-sm" />
      </div>
      <div className="flex gap-1.5 overflow-x-auto scrollbar-none pb-0.5">
        <select value={industryFilter} onChange={e => setIndustryFilter(e.target.value)} className="input text-[11px] py-1.5 flex-shrink-0">
          {INDUSTRIES.map(i => <option key={i} value={i}>{i === 'All' ? 'All industries' : i}</option>)}
        </select>
        <select value={payRange} onChange={e => setPayRange(e.target.value)} className="input text-[11px] py-1.5 flex-shrink-0">
          <option value="all">Any pay</option><option value="30+">$30+/hr</option><option value="35+">$35+/hr</option><option value="40+">$40+</option>
        </select>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="input text-[11px] py-1.5 flex-shrink-0">
          <option value="newest">Newest</option><option value="pay-high">Highest pay</option><option value="pay-low">Lowest pay</option>{hasLocation && <option value="near-me">Near me</option>}
        </select>
        {(searchQuery || industryFilter !== 'All' || payRange !== 'all') && (
          <button onClick={() => { setSearchQuery(''); setIndustryFilter('All'); setPayRange('all'); setSortBy('newest'); }}
            className="text-[11px] text-rose-500 px-2.5 py-1.5 hover:bg-rose-50 rounded-lg font-medium flex-shrink-0">Clear</button>
        )}
      </div>
    </div>
  );
}

// ─── Section header ─────────────────────────────────────────────
function SectionHeader({ count, color, icon, title, subtitle }) {
  return (
    <div className={`rounded-2xl p-5 mb-5 text-white ${color}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg">{icon}</span>
            <p className="font-brand text-lg font-extrabold leading-tight">{title}</p>
          </div>
          <p className="text-[13px] text-white/70 leading-relaxed">{subtitle}</p>
        </div>
        <div className="text-right ml-4 flex-shrink-0">
          <p className="text-3xl font-extrabold tabular-nums">{count}</p>
          <p className="text-[11px] text-white/60 font-medium">available</p>
        </div>
      </div>
    </div>
  );
}

// ─── Empty state ────────────────────────────────────────────────
function EmptyState({ title, sub, action, actionLabel }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-14 h-14 rounded-full bg-neutral-100 flex items-center justify-center mb-4">
        <svg className="w-6 h-6 text-neutral-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
      </div>
      <p className="text-sm font-semibold text-neutral-700 mb-1">{title}</p>
      <p className="text-xs text-neutral-400 mb-4">{sub}</p>
      {action && <button onClick={action} className="btn-primary btn-sm">{actionLabel}</button>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════
export default function WorkerDashboard({ user, onLogout }) {
  const http = api();
  const [shifts, setShifts]             = useState([]);
  const [applications, setApplications] = useState([]);
  const [tab, setTab]                   = useState('home');
  const [toast, setToast]               = useState({ msg: '', type: 'ok' });
  const [savedShiftIds, setSavedShiftIds] = useState(new Set());
  const [savedShifts, setSavedShifts]   = useState([]);
  const [completedHires, setCompletedHires] = useState([]);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [confirmingId, setConfirmingId] = useState(null);
  const [urgentShifts, setUrgentShifts] = useState({ entry_level: [], experienced: [], total: 0 });
  const [messages, setMessages]         = useState([]);
  const [skipped, setSkipped]           = useState(new Set());
  const [userLocation, setUserLocation] = useState(null);
  const [cancellingApp, setCancellingApp] = useState(null);
  const [showBetaModal, setShowBetaModal] = useState(() => !localStorage.getItem('beta_welcomed'));
  const [referralData, setReferralData] = useState(null);

  // Filters
  const [searchQuery,    setSearchQuery]    = useState('');
  const [industryFilter, setIndustryFilter] = useState('All');
  const [payRange,       setPayRange]       = useState('all');
  const [sortBy,         setSortBy]         = useState('newest');
  const [showOnboarding3Step, setShowOnboarding3Step] = useState(false);
  const [showQualificationAgent, setShowQualificationAgent] = useState(false);
  const [expandedApplicationGroups, setExpandedApplicationGroups] = useState({
    pending: true,
    hired: true,
    declined: false
  });

  let userData = user || {};
  try { if (!user) { const stored = localStorage.getItem('user'); userData = stored ? JSON.parse(stored) : {}; } } catch { userData = {}; }
  const firstName = ((userData && userData.name) ? userData.name : 'there').split(' ')[0];

  useEffect(() => { loadData(); loadSaved(); loadHistory(); loadUrgent(); getUserLocation(); checkOnboarding(); loadReferrals(); }, []);

  const checkOnboarding = async () => {
    try {
      const res = await api().get('/users/profile');
      if (res.data && !res.data.completed_onboarding_3step) {
        setShowOnboarding3Step(true);
      }
      // Show qualification agent if profile is complete but not qualified yet
      if (res.data?.completed_onboarding_3step && !localStorage.getItem('sm_profile_qualified')) {
        setTimeout(() => setShowQualificationAgent(true), 800);
      }
    } catch (err) {
      console.error('Failed to check onboarding status:', err);
    }
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding3Step(false);
    loadData(); // Refresh data
  };

  const getUserLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => { /* silent — location is optional */ },
      { enableHighAccuracy: false, timeout: 5000 }
    );
  };

  // Haversine distance in km
  const calcDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2-lat1) * Math.PI/180;
    const dLon = (lon2-lon1) * Math.PI/180;
    const a = Math.sin(dLat/2)*Math.sin(dLat/2) + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLon/2)*Math.sin(dLon/2);
    const clampedA = Math.min(1, Math.max(0, a));
    return R * 2 * Math.atan2(Math.sqrt(clampedA), Math.sqrt(1-clampedA));
  };

  // Parse shift location to approximate lat/lng (Sydney suburbs)
  const SUBURB_COORDS = {
    'sydney cbd': { lat: -33.8688, lng: 151.2093 }, 'sydney': { lat: -33.8688, lng: 151.2093 },
    'parramatta': { lat: -33.8151, lng: 151.0011 }, 'bondi': { lat: -33.8914, lng: 151.2743 },
    'surry hills': { lat: -33.8838, lng: 151.2120 }, 'newtown': { lat: -33.8977, lng: 151.1790 },
    'chatswood': { lat: -33.7969, lng: 151.1831 }, 'manly': { lat: -33.7971, lng: 151.2873 },
    'darlinghurst': { lat: -33.8779, lng: 151.2178 }, 'redfern': { lat: -33.8932, lng: 151.2044 },
    'randwick': { lat: -33.9144, lng: 151.2414 }, 'marrickville': { lat: -33.9107, lng: 151.1546 },
    'alexandria': { lat: -33.9038, lng: 151.1946 }, 'mascot': { lat: -33.9261, lng: 151.1876 },
    'zetland': { lat: -33.9069, lng: 151.2100 }, 'north sydney': { lat: -33.8389, lng: 151.2070 },
    'melbourne': { lat: -37.8136, lng: 144.9631 },
  };

  const getShiftDistance = (shift) => {
    if (!userLocation || !shift.location) return null;
    const loc = shift.location.toLowerCase().trim();
    // Try direct suburb match
    for (const [suburb, coords] of Object.entries(SUBURB_COORDS)) {
      if (loc.includes(suburb)) {
        return calcDistance(userLocation.lat, userLocation.lng, coords.lat, coords.lng);
      }
    }
    // Default: Sydney CBD
    return calcDistance(userLocation.lat, userLocation.lng, -33.8688, 151.2093);
  };

  const loadUrgent = async () => { try { const r = await http.get('/shifts/urgent'); setUrgentShifts(r.data || { entry_level: [], experienced: [], total: 0 }); } catch { /* silent */ } };
  const loadData   = async () => {
    try {
      const [s, a, m] = await Promise.all([http.get('/shifts'), http.get('/applications'), http.get('/messages').catch(() => ({ data: [] }))]);
      setShifts(s.data || []); setApplications(a.data || []); setMessages(m.data || []);
    } catch { /* silent */ }
  };
  const loadReferrals = async () => {
    try { const r = await http.get('/referrals'); setReferralData(r.data); } catch { /* silent */ }
  };
  const loadHistory = async () => {
    try {
      const r = await http.get('/worker/history');
      const completed = (r.data || []).filter(h => h.checked_out_at);
      setCompletedHires(completed);
      setTotalEarnings(completed.reduce((s, h) => s + (parseFloat(h.hourly_rate||0) * parseFloat(h.hours_worked||0)), 0));
    } catch { /* silent */ }
  };
  const loadSaved = async () => {
    try { const r = await http.get('/shifts/saved-list'); const list = r.data || []; setSavedShifts(list); setSavedShiftIds(new Set(list.map(s => s.id))); } catch { /* silent */ }
  };

  const toggleSave = async (shiftId) => {
    try {
      const r = await http.post(`/shifts/${shiftId}/save`);
      if (r.data.saved) { showToast('Saved'); setSavedShiftIds(p => new Set([...p, shiftId])); }
      else { showToast('Removed'); setSavedShiftIds(p => { const n = new Set(p); n.delete(shiftId); return n; }); }
      loadSaved();
    } catch { /* silent */ }
  };
  const applyShift = async (shift) => {
    try { await http.post('/applications', { shift_id: shift.id }); showToast(`Applied — "${(shift.title||'').replace(/^URGENT:\s*/i,'').slice(0,30)}"`); loadData(); } catch (e) { showToast(e.response?.data?.error || 'Could not apply', 'err'); }
  };
  const confirmShift = async (appId) => {
    setConfirmingId(appId);
    try { await http.put(`/applications/${appId}/confirm`); showToast('Shift confirmed'); loadData(); } catch (e) { showToast(e.response?.data?.error || 'Could not confirm', 'err'); }
    setConfirmingId(null);
  };
  const withdrawApp = async (appId) => {
    try { await http.put(`/applications/${appId}/withdraw`); showToast('Withdrawn'); loadData(); } catch (e) { showToast(e.response?.data?.error || 'Cannot withdraw', 'err'); }
  };
  const checkIn = async (shiftId, code) => {
    if (!code?.trim()) { showToast('Enter shift code from employer', 'err'); return; }
    try { await http.post(`/shifts/${shiftId}/checkin`, { shift_code: code }); showToast('Checked in'); loadData(); } catch (e) { showToast(e.response?.data?.error || 'Check-in failed', 'err'); }
  };
  const checkOut = async (shiftId) => {
    try { const r = await http.post(`/shifts/${shiftId}/checkout`); showToast(`Checked out — ${r.data.hours_worked}h worked`); loadData(); loadHistory(); } catch (e) { showToast(e.response?.data?.error || 'Check-out failed', 'err'); }
  };
  const handleLogout = () => { localStorage.removeItem('token'); localStorage.removeItem('user'); if (onLogout) onLogout(); };
  const showToast = (msg, type = 'ok') => { setToast({ msg, type }); setTimeout(() => setToast({ msg: '', type: 'ok' }), 4000); };
  const skipShift  = (id) => setSkipped(p => new Set([...p, id]));

  // Computed
  const appliedIds   = new Set(applications.map(a => a.shift_id));
  const hired        = applications.filter(a => a.status === 'hired');
  const openShifts   = shifts.filter(s => s.status === 'open');

  // Category logic (strict)
  // Urgent = is_urgent === true, any exp level
  const urgentAll   = [...(Array.isArray(urgentShifts.entry_level) ? urgentShifts.entry_level : []), ...(Array.isArray(urgentShifts.experienced) ? urgentShifts.experienced : [])].filter(s => !skipped.has(s.id));
  // No experience = entry_level true, NOT urgent (to avoid overlap)
  const noExpShifts = openShifts.filter(s => (s.entry_level === true || s.entry_level === 'true') && !s.is_urgent);
  // Qualified = entry_level false (requires certs/exp)
  const qualShifts  = openShifts.filter(s => s.entry_level === false || s.entry_level === 'false');

  const filterShifts = (list) => {
    let f = [...list];
    if (searchQuery.trim()) { const q = searchQuery.toLowerCase(); f = f.filter(s => (s.title||'').toLowerCase().includes(q) || (s.industry||'').toLowerCase().includes(q)); }
    if (industryFilter !== 'All') f = f.filter(s => (s.industry||'').toLowerCase() === industryFilter.toLowerCase());
    if (payRange === '30+') f = f.filter(s => (s.hourly_rate||0) >= 30);
    if (payRange === '35+') f = f.filter(s => (s.hourly_rate||0) >= 35);
    if (payRange === '40+') f = f.filter(s => (s.hourly_rate||0) >= 40);
    if (sortBy === 'pay-high') f.sort((a,b) => (b.hourly_rate||0)-(a.hourly_rate||0));
    else if (sortBy === 'pay-low') f.sort((a,b) => (a.hourly_rate||0)-(b.hourly_rate||0));
    else if (sortBy === 'near-me' && userLocation) f.sort((a,b) => (getShiftDistance(a)||999) - (getShiftDistance(b)||999));
    else f.sort((a,b) => new Date(b.created_at||0)-new Date(a.created_at||0));
    return f;
  };

  const filtNoExp  = useMemo(() => filterShifts(noExpShifts),  [noExpShifts,  searchQuery, industryFilter, payRange, sortBy]);
  const filtQual   = useMemo(() => filterShifts(qualShifts),   [qualShifts,   searchQuery, industryFilter, payRange, sortBy]);
  const filtUrgent = useMemo(() => urgentAll.filter(s => {
    if (searchQuery && !(s.title||'').toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (industryFilter !== 'All' && (s.industry||'').toLowerCase() !== industryFilter.toLowerCase()) return false;
    return true;
  }), [urgentAll, searchQuery, industryFilter]);

  const completedApps = applications.filter(a => a.status === 'completed');
  const confirmedEarnings = completedApps.reduce((sum, a) => { const s = shifts.find(x => x.id === a.shift_id); return sum + (s ? (s.hourly_rate||0)*(s.hours||0) : 0); }, 0);

  // Reliability score — avoid double-counting completed hires
  const completedAppCount = applications.filter(a => a.status === 'completed').length;
  const hiredAppCount = applications.filter(a => a.status === 'hired').length;
  const totalCompleted = completedAppCount + (Array.isArray(completedHires) ? completedHires.filter(h => !applications.some(a => a.id === h.application_id || a.shift_id === h.shift_id)).length : 0);
  const totalAllocations = hiredAppCount + completedAppCount + completedHires.length;
  const reliabilityPct = totalAllocations > 0 ? Math.round((totalCompleted / totalAllocations) * 100) : 100;

  // ── Bottom nav items ─────────────────────────────────────────
  const NAV = [
    {
      id: 'home', label: 'Home',
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/></svg>
    },
    {
      id: 'urgent', label: 'Urgent',
      badge: urgentAll.length,
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
    },
    {
      id: 'noexp', label: 'Easy',
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
    },
    {
      id: 'qualified', label: 'Skilled',
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/></svg>
    },
    {
      id: 'me', label: 'Me',
      badge: applications.length,
      icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
    },
  ];

  // ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-surface-muted pb-24">
      <SEOHelmet title="ShiftMatch — Dashboard" description="Find casual shifts across Australia." />
      <OnboardingTour role="worker" />
      {showBetaModal && (
        <BetaWelcomeModal role="worker" onDismiss={() => setShowBetaModal(false)} />
      )}
      {showOnboarding3Step && (
        <WorkerOnboarding3Step userId={userData.id} onComplete={handleOnboardingComplete} />
      )}

      {/* Toast */}
      {toast.msg && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-[100] px-5 py-2.5 rounded-2xl text-sm font-semibold shadow-elevated glass animate-slide-up whitespace-nowrap ${toast.type === 'err' ? 'text-rose-600' : 'text-brand-600'}`}>
          {toast.msg}
        </div>
      )}

      {/* ── Header ── */}
      <header className="glass border-b border-neutral-100/60 sticky top-0 z-50 safe-top">
        <div className="max-w-lg mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo-icon.svg" alt="" className="w-7 h-7" />
            <span className="font-brand font-bold text-[15px] text-neutral-900 tracking-tight">ShiftMatch</span>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={() => setTab('messages')} className="relative p-2 rounded-full hover:bg-neutral-100 transition" aria-label="Messages">
              <svg className="w-5 h-5 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
              {messages.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-brand-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{messages.length > 9 ? '9+' : messages.length}</span>}
            </button>
            <NotificationBell user={user} />
            <button onClick={handleLogout} className="text-[11px] text-neutral-400 hover:text-rose-500 font-medium px-2.5 py-1.5 rounded-lg hover:bg-rose-50/60 transition-all">
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* ── Content ── */}
      <main className="max-w-lg mx-auto px-5 pt-5">

        {/* ══════════════════════════════════════════════════════════
            HOME
        ══════════════════════════════════════════════════════════ */}
        {tab === 'home' && (
          <div className="animate-fade-in">
            {/* Greeting */}
            <div className="mb-5">
              <h2 className="font-brand text-2xl font-extrabold text-neutral-900 tracking-tight">
                Hey {firstName}
              </h2>
              <p className="text-sm text-neutral-400 mt-0.5">
                {urgentAll.length > 0
                  ? <span>You have <span className="text-rose-500 font-semibold">{urgentAll.length} urgent shifts</span> near you</span>
                  : `${openShifts.length} shifts available right now`}
              </p>
            </div>

            {/* Earnings card */}
            <div className="gradient-brand rounded-2xl p-5 mb-5 shadow-glow-green">
              <p className="text-[11px] font-semibold text-white/60 uppercase tracking-widest mb-1">Total earned</p>
              <p className="text-4xl font-extrabold text-white tabular-nums">${(confirmedEarnings + totalEarnings).toFixed(0)}</p>
              <div className="h-px bg-white/10 my-3" />
              <div className="flex items-center justify-between text-[12px] text-white/60">
                <span>{hired.length} hired &nbsp;·&nbsp; {applications.length} applied</span>
                <span className="font-semibold text-white/80">{completedHires.length} completed</span>
              </div>
            </div>

            {/* Next Shift Card (retention loop) */}
            <div className="mb-6">
              <NextShiftCard />
            </div>

            {/* Three quick-access cards (browsing shortcuts) */}
            <div className="grid grid-cols-3 gap-2.5 mb-6">
              <button onClick={() => setTab('urgent')}
                className="rounded-2xl bg-gradient-to-br from-rose-500 to-orange-400 p-3.5 text-left text-white shadow-sm active:scale-95 transition-all">
                <div className="text-[22px] font-extrabold tabular-nums">{urgentAll.length}</div>
                <div className="text-[11px] text-white/80 font-semibold leading-tight mt-0.5">Urgent<br/>shifts</div>
                <div className="w-1.5 h-1.5 rounded-full bg-white/60 mt-2 animate-pulse-urgent" />
              </button>
              <button onClick={() => setTab('noexp')}
                className="rounded-2xl bg-gradient-to-br from-brand-500 to-emerald-400 p-3.5 text-left text-white shadow-sm active:scale-95 transition-all">
                <div className="text-[22px] font-extrabold tabular-nums">{noExpShifts.length}</div>
                <div className="text-[11px] text-white/80 font-semibold leading-tight mt-0.5">No exp<br/>needed</div>
              </button>
              <button onClick={() => setTab('qualified')}
                className="rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-500 p-3.5 text-left text-white shadow-sm active:scale-95 transition-all">
                <div className="text-[22px] font-extrabold tabular-nums">{qualShifts.length}</div>
                <div className="text-[11px] text-white/80 font-semibold leading-tight mt-0.5">Skilled<br/>work</div>
              </button>
            </div>

            {/* Swipeable urgent preview */}
            {urgentAll.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse-urgent flex-shrink-0" />
                    <h3 className="font-brand text-sm font-bold text-neutral-900">Filling now — swipe to apply</h3>
                  </div>
                  <button onClick={() => setTab('urgent')} className="text-[11px] text-brand-600 font-semibold">See all</button>
                </div>
                <div className="space-y-2.5">
                  {urgentAll.slice(0,2).map(s => (
                    <SwipeCard key={s.id} shift={s} urgent applied={appliedIds.has(s.id)} saved={savedShiftIds.has(s.id)} onApply={applyShift} onSave={toggleSave} onSkip={skipShift} distance={sortBy === "near-me" ? getShiftDistance(s) : null} />
                  ))}
                </div>
              </div>
            )}

            {/* Recent no-exp preview */}
            {noExpShifts.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-brand text-sm font-bold text-neutral-900">No experience needed</h3>
                  <button onClick={() => setTab('noexp')} className="text-[11px] text-brand-600 font-semibold">See all</button>
                </div>
                <div className="space-y-2.5">
                  {noExpShifts.slice(0,2).map(s => (
                    <SwipeCard key={s.id} shift={s} applied={appliedIds.has(s.id)} saved={savedShiftIds.has(s.id)} onApply={applyShift} onSave={toggleSave} onSkip={skipShift} distance={sortBy === "near-me" ? getShiftDistance(s) : null} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            URGENT WORK — same-day / last-minute, any experience
        ══════════════════════════════════════════════════════════ */}
        {tab === 'urgent' && (
          <div className="animate-fade-in">
            <SectionHeader
              count={urgentAll.length}
              color="bg-gradient-to-br from-rose-500 to-orange-500"
              icon="⚡"
              title="Urgent Work"
              subtitle="Same-day & last-minute shifts. These employers need someone now — apply instantly."
            />

            {/* Filter row */}
            <div className="flex gap-1.5 overflow-x-auto scrollbar-none mb-4">
              <select value={industryFilter} onChange={e => setIndustryFilter(e.target.value)} className="input text-[11px] py-1.5 flex-shrink-0">
                {INDUSTRIES.map(i => <option key={i} value={i}>{i === 'All' ? 'All industries' : i}</option>)}
              </select>
              {industryFilter !== 'All' && (
                <button onClick={() => setIndustryFilter('All')} className="text-[11px] text-rose-500 px-2.5 py-1.5 hover:bg-rose-50 rounded-lg font-medium flex-shrink-0">Clear</button>
              )}
            </div>

            {/* Tip: swipe to apply */}
            <div className="flex items-center gap-2 bg-neutral-50 rounded-xl px-3 py-2 mb-4">
              <span className="text-[11px] text-neutral-400 font-medium">Tip: swipe right to apply instantly, swipe left to skip</span>
            </div>

            {filtUrgent.length > 0 ? (
              <div className="space-y-3">
                {filtUrgent.map(s => (
                  <SwipeCard key={s.id} shift={s} urgent applied={appliedIds.has(s.id)} saved={savedShiftIds.has(s.id)} onApply={applyShift} onSave={toggleSave} onSkip={skipShift} distance={sortBy === "near-me" ? getShiftDistance(s) : null} />
                ))}
              </div>
            ) : (
              <EmptyState title="No urgent shifts right now" sub="Check back soon — these go fast." action={() => setTab('noexp')} actionLabel="Browse easy work" />
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            NO EXPERIENCE — entry_level=true, right to work only
        ══════════════════════════════════════════════════════════ */}
        {tab === 'noexp' && (
          <div className="animate-fade-in">
            <SectionHeader
              count={noExpShifts.length}
              color="bg-gradient-to-br from-brand-500 to-emerald-400"
              icon="✓"
              title="No Experience Needed"
              subtitle="Just your right to work and a valid ID. No CV, no interview, no qualifications required."
            />

            <SearchBar {...{ searchQuery, setSearchQuery, industryFilter, setIndustryFilter, payRange, setPayRange, sortBy, setSortBy, hasLocation: !!userLocation }} />

            {filtNoExp.length > 0 ? (
              <div className="space-y-3">
                {filtNoExp.map(s => (
                  <SwipeCard key={s.id} shift={s} applied={appliedIds.has(s.id)} saved={savedShiftIds.has(s.id)} onApply={applyShift} onSave={toggleSave} onSkip={skipShift} distance={sortBy === "near-me" ? getShiftDistance(s) : null} />
                ))}
              </div>
            ) : (
              <EmptyState title="No matches" sub="Try removing filters." action={() => { setSearchQuery(''); setIndustryFilter('All'); setPayRange('all'); }} actionLabel="Clear filters" />
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            QUALIFIED WORK — certifications, licences, experience
        ══════════════════════════════════════════════════════════ */}
        {tab === 'qualified' && (
          <div className="animate-fade-in">
            <SectionHeader
              count={qualShifts.length}
              color="bg-gradient-to-br from-blue-600 to-indigo-600"
              icon="★"
              title="Qualified Work"
              subtitle="Your certifications earn more. RSA, White Card, Security Licence, Forklift — put them to work."
            />

            <SearchBar {...{ searchQuery, setSearchQuery, industryFilter, setIndustryFilter, payRange, setPayRange, sortBy, setSortBy, hasLocation: !!userLocation }} />

            {filtQual.length > 0 ? (
              <div className="space-y-3">
                {filtQual.map(s => (
                  <SwipeCard key={s.id} shift={s} applied={appliedIds.has(s.id)} saved={savedShiftIds.has(s.id)} onApply={applyShift} onSave={toggleSave} onSkip={skipShift} distance={sortBy === "near-me" ? getShiftDistance(s) : null} />
                ))}
              </div>
            ) : (
              <EmptyState title="No skilled shifts match" sub="Try different filters or check No Experience section." action={() => setTab('noexp')} actionLabel="Browse easy shifts" />
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            ME — applied, earned, saved, account
        ══════════════════════════════════════════════════════════ */}
        {tab === 'me' && (
          <div className="animate-fade-in">
            {/* Profile header */}
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-white font-bold text-lg">
                {firstName[0]?.toUpperCase()}
              </div>
              <div>
                <p className="font-brand font-bold text-neutral-900">{userData.name || 'Worker'}</p>
                <p className="text-xs text-neutral-400">{userData.email}</p>
              </div>
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-2 mb-6">
              <div className="card p-3 text-center">
                <p className="text-xl font-extrabold text-brand-600 tabular-nums">${(confirmedEarnings + totalEarnings).toFixed(0)}</p>
                <p className="text-[10px] text-neutral-400 font-medium mt-0.5">Earned</p>
              </div>
              <div className="card p-3 text-center">
                <p className="text-xl font-extrabold text-blue-600 tabular-nums">{applications.length}</p>
                <p className="text-[10px] text-neutral-400 font-medium mt-0.5">Applied</p>
              </div>
              <div className="card p-3 text-center">
                <p className={`text-xl font-extrabold tabular-nums ${reliabilityPct >= 80 ? 'text-brand-600' : reliabilityPct >= 50 ? 'text-amber-600' : 'text-rose-600'}`}>{reliabilityPct}%</p>
                <p className="text-[10px] text-neutral-400 font-medium mt-0.5">Reliability</p>
              </div>
            </div>

            {/* Applications section with collapsible groups */}
            {applications.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xs font-bold text-neutral-700 uppercase tracking-wider mb-3">Your applications</h3>
                
                {/* PENDING GROUP */}
                {(() => {
                  const pending = applications.filter(a => a.status === 'pending');
                  return pending.length > 0 ? (
                    <div className="mb-3 border border-amber-100 rounded-xl overflow-hidden">
                      <button
                        onClick={() => setExpandedApplicationGroups(p => ({ ...p, pending: !p.pending }))}
                        className="w-full p-3.5 bg-amber-50 hover:bg-amber-100 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-amber-600 font-bold text-sm">⏳ Pending</span>
                          <span className="text-xs bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full font-semibold">{pending.length}</span>
                        </div>
                        <svg className={`w-4 h-4 text-amber-600 transition-transform ${expandedApplicationGroups.pending ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 9l6 6 6-6"/></svg>
                      </button>
                      {expandedApplicationGroups.pending && (
                        <div className="space-y-2 p-3.5 border-t border-amber-100">
                          {pending.map(a => {
                            const s = shifts.find(x => x.id === a.shift_id);
                            return (
                              <div key={a.id} className="p-3 bg-white rounded-lg border border-amber-50">
                                <div className="flex items-start justify-between mb-1">
                                  <h4 className="text-xs font-bold text-neutral-900 truncate flex-1">{(s?.title||'Shift').replace(/^URGENT:\s*/i,'')}</h4>
                                  <button onClick={() => withdrawApp(a.id)} className="text-[9px] text-rose-400 hover:text-rose-600 font-semibold ml-2 flex-shrink-0">Withdraw</button>
                                </div>
                                <p className="text-[10px] text-neutral-400">{s?.location || 'Sydney'} · ${Math.round(s?.hourly_rate||0)}/hr · {s?.hours || '?'}h</p>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : null;
                })()}
                
                {/* HIRED GROUP */}
                {(() => {
                  const hiredApps = applications.filter(a => a.status === 'hired');
                  return hiredApps.length > 0 ? (
                    <div className="mb-3 border border-brand-100 rounded-xl overflow-hidden">
                      <button
                        onClick={() => setExpandedApplicationGroups(p => ({ ...p, hired: !p.hired }))}
                        className="w-full p-3.5 bg-brand-50 hover:bg-brand-100 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-brand-600 font-bold text-sm">✓ Hired</span>
                          <span className="text-xs bg-brand-200 text-brand-900 px-2 py-0.5 rounded-full font-semibold">{hiredApps.length}</span>
                        </div>
                        <svg className={`w-4 h-4 text-brand-600 transition-transform ${expandedApplicationGroups.hired ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 9l6 6 6-6"/></svg>
                      </button>
                      {expandedApplicationGroups.hired && (
                        <div className="space-y-2 p-3.5 border-t border-brand-100">
                          {hiredApps.map(a => {
                            const s = shifts.find(x => x.id === a.shift_id);
                            return (
                              <div key={a.id} className="p-3 bg-white rounded-lg border border-brand-50">
                                <div className="flex items-start justify-between mb-1">
                                  <h4 className="text-xs font-bold text-neutral-900 truncate flex-1">{(s?.title||'Shift').replace(/^URGENT:\s*/i,'')}</h4>
                                  <button onClick={() => confirmShift(a.id)} disabled={confirmingId === a.id} className="text-[9px] font-semibold bg-brand-500 hover:bg-brand-600 text-white px-2 py-0.5 rounded ml-2 flex-shrink-0 disabled:opacity-50">{confirmingId === a.id ? '...' : 'Confirm'}</button>
                                </div>
                                <p className="text-[10px] text-neutral-400 mb-2">{s?.location || 'Sydney'} · ${Math.round(s?.hourly_rate||0)}/hr · {s?.hours || '?'}h</p>
                                <div className="flex flex-wrap gap-1 text-[9px]">
                                  <input id={`ci-${a.id}`} placeholder="Shift code" className="input text-[9px] py-1 px-2 flex-1 min-w-[60px]" />
                                  <button onClick={() => checkIn(a.shift_id, document.getElementById(`ci-${a.id}`)?.value)} className="btn-secondary text-[9px] py-1 px-2">Check in</button>
                                  <button onClick={() => checkOut(a.shift_id)} className="btn-secondary text-[9px] py-1 px-2">Check out</button>
                                  <button onClick={() => setCancellingApp(a)} className="text-rose-400 hover:text-rose-600 font-semibold px-2 py-1">Cancel</button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : null;
                })()}
                
                {/* DECLINED GROUP */}
                {(() => {
                  const declined = applications.filter(a => a.status === 'declined');
                  return declined.length > 0 ? (
                    <div className="border border-neutral-100 rounded-xl overflow-hidden">
                      <button
                        onClick={() => setExpandedApplicationGroups(p => ({ ...p, declined: !p.declined }))}
                        className="w-full p-3.5 bg-neutral-50 hover:bg-neutral-100 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-neutral-500 font-bold text-sm">✗ Not selected</span>
                          <span className="text-xs bg-neutral-200 text-neutral-700 px-2 py-0.5 rounded-full font-semibold">{declined.length}</span>
                        </div>
                        <svg className={`w-4 h-4 text-neutral-500 transition-transform ${expandedApplicationGroups.declined ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 9l6 6 6-6"/></svg>
                      </button>
                      {expandedApplicationGroups.declined && (
                        <div className="space-y-2 p-3.5 border-t border-neutral-100">
                          {declined.map(a => {
                            const s = shifts.find(x => x.id === a.shift_id);
                            return (
                              <div key={a.id} className="p-3 bg-white rounded-lg border border-neutral-50">
                                <h4 className="text-xs font-bold text-neutral-600 truncate mb-0.5">{(s?.title||'Shift').replace(/^URGENT:\s*/i,'')}</h4>
                                <p className="text-[10px] text-neutral-400">{s?.location || 'Sydney'} · ${Math.round(s?.hourly_rate||0)}/hr</p>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : null;
                })()}
              </div>
            )}

            {/* ────────────────────────────────────────────────────── */}
            {/* REFERRAL & REWARDS SECTION (standalone) */}
            {/* ────────────────────────────────────────────────────── */}
            {referralData && (
              <div className="mb-8">
                <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-5 border border-violet-100">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🎁</span>
                    <h3 className="font-bold text-violet-900 text-base">Refer & Earn $20</h3>
                  </div>
                  <p className="text-sm text-violet-700 mb-4 leading-relaxed">Share your link — earn $20 for every friend who completes their first shift</p>
                  <div className="flex gap-2 mb-4">
                    <input
                      readOnly
                      value={referralData.referral_link || ''}
                      className="flex-1 text-xs bg-white border border-violet-200 rounded-lg px-3 py-2.5 text-violet-800 truncate font-mono"
                    />
                    <button
                      onClick={() => { navigator.clipboard.writeText(referralData.referral_link || ''); showToast('Link copied!'); }}
                      className="px-4 py-2.5 bg-violet-600 text-white text-xs font-bold rounded-lg hover:bg-violet-700 transition-colors flex-shrink-0"
                    >Copy</button>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white rounded-lg p-3 text-center border border-violet-100">
                      <p className="text-lg font-extrabold text-violet-700 tabular-nums">{referralData.total_credited || 0}</p>
                      <p className="text-[10px] text-violet-500 font-medium mt-0.5">Credited</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border border-green-100">
                      <p className="text-lg font-extrabold text-green-600 tabular-nums">${referralData.wallet_balance?.toFixed(0) || '0'}</p>
                      <p className="text-[10px] text-green-500 font-medium mt-0.5">Wallet</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border border-neutral-100">
                      <p className="text-lg font-extrabold text-neutral-600 tabular-nums">{(referralData.referrals || []).length}</p>
                      <p className="text-[10px] text-neutral-400 font-medium mt-0.5">Invited</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ─────────────────────────────────────────────────────────── */}
            {/* PROFILE & RESOURCES SECTION (standalone with better spacing) */}
            {/* ─────────────────────────────────────────────────────────── */}
            <div className="mb-6">
              <div className="space-y-2.5">
                {[
                  { label: 'Set Availability', sub: `${(() => { try { const d = localStorage.getItem('sm_availability'); return d ? Object.keys(JSON.parse(d)).length : 0; } catch { return 0; } })()} days set`, action: () => setTab('availability'), icon: <svg className="w-5 h-5 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg> },
                  { label: 'Saved shifts',    sub: `${savedShifts.length} saved`,              action: () => setTab('saved'), icon: <svg className="w-5 h-5 text-rose-400" fill="currentColor" viewBox="0 0 24 24"><path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg> },
                  { label: 'Earnings history', sub: `$${(confirmedEarnings+totalEarnings).toFixed(0)} earned`,  action: () => setTab('earnings'), icon: <svg className="w-5 h-5 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg> },
                  { label: 'Messages',        sub: `${messages.length} conversations`,          action: () => setTab('messages'), icon: <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg> },
                  { label: 'Settings',        sub: 'Profile, notifications',                    href: '/settings', icon: <svg className="w-5 h-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.573-1.066z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg> },
                  { label: 'My Licences 🩺',   sub: 'RSA, White Card, Forklift...',             action: () => setTab('licences'), icon: <svg className="w-5 h-5 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z"/></svg> },
                  { label: 'Certifications',  sub: 'Manage licences & certs',                   href: '/certifications', icon: <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/></svg> },
                  { label: 'Training',        sub: 'Upskill and earn more',                     href: '/training', icon: <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/></svg> },
                  { label: 'Insurance',       sub: 'Protect yourself on shifts',                href: '/insurance', icon: <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg> },
                  { label: 'Visa Agent 🛂',    sub: 'Free visa tracking & alerts',                 href: '/visa-agent', icon: <svg className="w-5 h-5 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg> },
                  { label: 'AbilityHub ♿', sub: 'Disability support, subsidies & rights',        href: '/ability-hub', icon: <svg className="w-5 h-5 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg> },
                  { label: 'Migrant Worker Hub 🌏', sub: 'Settle in Australia, save money',          href: '/migrant-hub', icon: <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg> },
                  { label: 'Refer a friend',  sub: 'Earn $20 per invite',                       href: '/referrals', icon: <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"/></svg> },
                ].map((item, i) => (
                  <button key={i} onClick={item.action || (() => window.location.href = item.href)}
                    className="w-full card p-4 flex items-center justify-between text-left hover:shadow-elevated hover:-translate-y-0.5 transition-all active:scale-[0.98]">
                    <div className="flex items-center gap-3">
                      {item.icon && <div className="flex-shrink-0">{item.icon}</div>}
                      <div>
                        <p className="text-sm font-semibold text-neutral-900">{item.label}</p>
                        <p className="text-[11px] text-neutral-400">{item.sub}</p>
                      </div>
                    </div>
                    <svg className="w-4 h-4 text-neutral-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
                  </button>
                ))}
              </div>
            </div>

            {/* Sign out button */}
            <button onClick={handleLogout} className="w-full mb-6 py-3 text-sm font-semibold text-neutral-400 hover:text-rose-500 bg-neutral-50 hover:bg-rose-50 rounded-2xl border border-neutral-100 hover:border-rose-200 transition-all">
              Sign out
            </button>
          </div>
        )}

        {tab === 'licences' && (
          <LicencesPage user={user} onBack={() => setTab('me')} />
        )}

        {/* Welfare Check Modal — shows automatically for workers with pending checks */}
        <WelfareCheckModal user={user} />

        {/* Profile Qualification Agent — guides workers to resources */}
        {showQualificationAgent && (
          <ProfileQualificationAgent
            user={user}
            onComplete={(selections) => {
              setShowQualificationAgent(false);
              // Store qualifications in localStorage for quick access
              localStorage.setItem('sm_worker_qualifications', JSON.stringify(selections));
            }}
            onClose={() => setShowQualificationAgent(false)}
          />
        )}

        {/* ══════ SUB-PAGES (accessible from Me tab) ══════ */}

        {/* Cancel Shift Modal */}
        {cancellingApp && (
          <CancelShiftModal
            app={cancellingApp}
            onCancel={() => { setCancellingApp(null); loadData(); showToast('Shift cancelled'); }}
            onClose={() => setCancellingApp(null)}
          />
        )}

        {tab === 'availability' && (
          <AvailabilityCalendar onBack={() => setTab('me')} />
        )}

        {tab === 'saved' && (
          <div className="animate-fade-in">
            <div className="flex items-center gap-2 mb-4">
              <button onClick={() => setTab('me')} className="p-1.5 rounded-lg hover:bg-neutral-100 transition-all"><svg className="w-4 h-4 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg></button>
              <h2 className="font-brand text-lg font-bold text-neutral-900">Saved shifts</h2>
            </div>
            {savedShifts.length > 0 ? (
              <div className="space-y-3">{savedShifts.map(s => (<SwipeCard key={s.id} shift={s} applied={appliedIds.has(s.id)} saved={true} onApply={applyShift} onSave={toggleSave} />))}</div>
            ) : <EmptyState title="No saved shifts" sub="Tap the heart on any shift to save it here." />}
          </div>
        )}

        {tab === 'earnings' && (
          <div className="animate-fade-in">
            <div className="flex items-center gap-2 mb-4">
              <button onClick={() => setTab('me')} className="p-1.5 rounded-lg hover:bg-neutral-100 transition-all"><svg className="w-4 h-4 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg></button>
              <h2 className="font-brand text-lg font-bold text-neutral-900">Earnings</h2>
            </div>
            <div className="gradient-brand rounded-2xl p-5 mb-5 shadow-glow-green">
              <p className="text-[11px] font-semibold text-white/60 uppercase tracking-widest mb-1">Total earned</p>
              <p className="text-4xl font-extrabold text-white tabular-nums">${(confirmedEarnings+totalEarnings).toFixed(0)}</p>
              <div className="flex gap-4 mt-3 text-[12px] text-white/60"><span>{completedHires.length} shifts completed</span><span>{hired.length} upcoming</span></div>
            </div>
            {completedHires.length > 0 ? (
              <div className="space-y-2">
                {completedHires.map(h => {
                  const s = shifts.find(x => x.id === h.shift_id);
                  return (
                    <div key={h.id} className="card p-4 flex items-center justify-between">
                      <div><p className="text-sm font-semibold text-neutral-900">{(s?.title||'Shift').replace(/^URGENT:\s*/i,'')}</p><p className="text-[11px] text-neutral-400">{new Date(h.checked_out_at||h.created_at).toLocaleDateString('en-AU',{weekday:'short',day:'numeric',month:'short'})} · {h.hours_worked||'?'}h</p></div>
                      <div className="text-right"><p className="text-sm font-bold text-brand-600 tabular-nums">${(parseFloat(h.hourly_rate||0)*parseFloat(h.hours_worked||0)).toFixed(0)}</p><p className="text-[10px] text-neutral-400">${Math.round(h.hourly_rate||0)}/hr</p></div>
                    </div>
                  );
                })}
              </div>
            ) : <EmptyState title="No earnings yet" sub="Complete your first shift to start earning." />}
          </div>
        )}

        {tab === 'messages' && (
          <div className="animate-fade-in">
            <div className="flex items-center gap-2 mb-4">
              <button onClick={() => setTab('me')} className="p-1.5 rounded-lg hover:bg-neutral-100 transition-all"><svg className="w-4 h-4 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg></button>
              <h2 className="font-brand text-lg font-bold text-neutral-900">Messages</h2>
            </div>
            {messages.length > 0 ? (
              <div className="space-y-2">{messages.map(m => (<div key={m.id} className="card p-4"><div className="flex items-center justify-between mb-1"><span className="text-sm font-semibold text-neutral-900">{m.sender_name||'Employer'}</span><span className="text-[10px] text-neutral-400">{new Date(m.created_at).toLocaleDateString()}</span></div><p className="text-[13px] text-neutral-600">{m.message||m.content}</p></div>))}</div>
            ) : <EmptyState title="No messages yet" sub="Messages from employers appear here." />}
          </div>
        )}

      </main>

      {/* ── Bottom nav ── */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 glass border-t border-neutral-100/60 safe-bottom">
        <div className="max-w-lg mx-auto flex items-stretch">
          {NAV.map(item => {
            const active = tab === item.id || (item.id === 'me' && ['saved','earnings','messages','availability'].includes(tab));
            return (
              <button key={item.id} onClick={() => setTab(item.id)}
                className={`flex-1 flex flex-col items-center justify-center py-2.5 transition-all duration-150 ${active ? 'text-brand-600' : 'text-neutral-400'}`}>
                <div className="relative">
                  {item.icon}
                  {item.badge > 0 && (
                    <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 bg-rose-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </div>
                <span className={`text-[10px] mt-0.5 ${active ? 'font-bold' : 'font-medium'}`}>{item.label}</span>
                {active && <div className="w-4 h-0.5 bg-brand-500 rounded-full mt-0.5" />}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
